import { GProductVertical } from '@gazin/ecommerce-ui'
import { useCallback, useMemo } from 'react'
import { LiveloBadgeText } from 'src/components/LiveloBadgeText'
import { useFavorite } from 'src/contexto/hooks/useFavorite'
import { usePaymentMethod } from 'src/contexto/hooks/usePaymentMethod'
import { theme } from 'src/styles/themes/default'
import { getFavoriteStoreKey } from 'src/util/favorite'
import { isHomolog, isProduction } from 'src/util/isProduction'
import {
  arePixOrBilletAvailable,
  getInCashAvailablePaymentMethods,
  isCreditCardAvailable,
} from 'src/util/payment'
import {
  hasPreviousPrice,
  resolveDiscountBadge,
} from 'src/util/promotionalEvent'
import { ThumborLoader } from 'src/util/thumborLoader'
import { CookieKeyTypeEnum, getCookieItem } from '../../util/cookie-domain'
import { Campaign } from '../../util/enums/Campaign'
import { ProductVerticalCard } from './styles'
import { colorAndVoltageStructure, formatVoltage } from './utils'

// TNDC-397: não derivar do breakpoint — a URL trocava na hidratação e baixava duas vezes.
// 300px cobre o card mobile (120px @2x) e o desktop (189px).
const CARD_IMAGE_WIDTH = 300

export type ProductCombination = {
  slug: string
  valor: string
}

export type PromoEventImage = {
  desktop?: string
  mobile?: string
}

export type ProductProps = {
  color: ProductCombination
  colorsQuantity: number
  hasVariablePrice?: boolean
  de: number
  id: number
  imageUrl: string
  imageWidth?: number
  installmentPriceWithInterest: number
  installmentMaxQuantity: number
  installmentMaxQuantityWithoutInterest: number
  isAvailable: boolean
  percent: number
  por: number
  price: number
  productUrl: string
  promoEventImage?: PromoEventImage
  reviewQuantity: number
  reviewRating: number
  showDiscountBadge?: boolean
  showPromoBadge?: boolean
  title: string
  trackingUrl?: string
  voltage: ProductCombination
}

export type ProductVerticalProps = {
  baseColor?: string
  border?: string
  isBlackFriday?: boolean
  isHotSite?: boolean
  isFavorite?: boolean
  isFluidMobile?: boolean
  isSearchAndCategory?: boolean
  maxLinesTitle?: number
  onClick?: (product: ProductProps) => void | Promise<void>
  product: ProductProps
  textColor?: string
}

export const ProductVertical = ({
  baseColor,
  border,
  isBlackFriday,
  isHotSite,
  isFavorite,
  isFluidMobile,
  isSearchAndCategory,
  onClick,
  product,
  textColor,
}: ProductVerticalProps) => {
  const { payment } = usePaymentMethod()
  const { favorite, favorites } = useFavorite()

  const favoriteKey = useMemo(
    () =>
      getFavoriteStoreKey(
        favorites,
        Number(product.id),
        product.color.slug,
        formatVoltage(product.voltage.slug)
      ),
    [favorites, product.color.slug, product.id, product.voltage.slug]
  )

  const handleClickFavorite = useCallback(() => {
    favorite(
      product.productUrl,
      Number(product.id),
      product.color.slug,
      formatVoltage(product.voltage.slug),
      favoriteKey
    )
  }, [
    favorite,
    favoriteKey,
    product.color.slug,
    product.id,
    product.productUrl,
    product.voltage.slug,
  ])

  const isComingFromLivelo =
    getCookieItem(CookieKeyTypeEnum.campaign) === Campaign.LIVELO

  let liveloText = undefined
  let showBadge = undefined

  if (isComingFromLivelo && product.isAvailable) {
    liveloText = <LiveloBadgeText price={product.por} />
    showBadge = true
  }

  let shareProductUrl

  if (isProduction() && product.productUrl) {
    shareProductUrl = `https://www.gazin.com.br${product.productUrl}`
  } else if (isHomolog() && product.productUrl) {
    shareProductUrl = `https://staging.gazin.com.br${product.productUrl}`
  } else {
    shareProductUrl = product.productUrl
  }

  return (
    <ProductVerticalCard
      href={product.productUrl}
      onClick={() => onClick?.(product)}
      isBlackFriday={isBlackFriday}
      isHotSite={isHotSite}
      isFavorite={isFavorite}
      isFluidMobile={isFluidMobile}
      isSearchAndCategory={isSearchAndCategory}
      data-testid={
        isFavorite
          ? `card-favorite-product-id-${product.id}`
          : `product-vertical-card`
      }
    >
      <GProductVertical
        border={border}
        urlProduct={shareProductUrl}
        isShare
        promoBadgeColor={textColor || theme.colors.white}
        de={product.de}
        por={product.por}
        price={product.price}
        hasVariablePrice={product.hasVariablePrice}
        installmentPriceWithInterest={product.installmentPriceWithInterest}
        installmentMaxQuantity={product.installmentMaxQuantity}
        installmentMaxQuantityWithoutInterest={
          product.installmentMaxQuantityWithoutInterest
        }
        percent={product.percent}
        title={product.title}
        maxLinesTitle={2}
        negativeSign
        isFavorite
        isAvailable={product.isAvailable}
        isCreditCardPaymentAvailable={isCreditCardAvailable(payment)}
        isInCashPaymentAvailable={arePixOrBilletAvailable(payment)}
        isPreviousPrice={hasPreviousPrice(product.de, product.por)}
        isPromo={resolveDiscountBadge(product.showDiscountBadge, {
          discountPercentage: product.percent,
          isAvailable: product.isAvailable,
        })}
        onClick={() => onClick?.(product)}
        onClickFavorite={handleClickFavorite}
        variant={favoriteKey >= 0 ? 'filled' : 'outline'}
        variantPromo={baseColor || theme.success.main}
        voltageColorVariation={colorAndVoltageStructure(product)}
        colorsQuantity={product.colorsQuantity}
        productUnavailableDescription="Produto Indisponível"
        cashPaymentDescription={`No ${getInCashAvailablePaymentMethods(
          payment
        )}`}
        desktopImage={product.promoEventImage?.desktop}
        mobileImage={product.promoEventImage?.mobile}
        urlImage={ThumborLoader({
          src: product.imageUrl,
          width: CARD_IMAGE_WIDTH,
          quality: 75,
        })}
        showLiveloGBadge={showBadge}
        liveloGBadgeText={liveloText}
        alt={product.title}
        reviewQuantity={product.reviewQuantity}
        reviewRating={product.reviewRating}
      />
    </ProductVerticalCard>
  )
}
